import * as React from 'react';
import { Heading } from './UpsertServiceForm';
import { PRODUCT_ADDONS_DOWNLOAD_TEMPLATE, PRODUCT_ADDONS_IMPORT_COMPLETION_SOCKET, PRODUCT_ADDONS_IMPORT_CONTAINER, PRODUCT_ADDONS_IMPORT_PROGRESS_SOCKET, SERVICE_CONSTANTS, SERVICE_FORM_FIELDS, SKU_QUANTITY } from 'constants/service';
import { GENERAL_CONSTANTS } from 'constants/general';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { IFormData } from 'data/Service';
import Flexbox from 'components/UI/FlexboxContainer';
import styled from 'styled-components';
import { renderCheckbox } from '../UI/RenderFields';
import ServiceProductsTable from './ServiceProductsTable';
import InputComponent from 'components/UI/Input';
import Button from 'components/UI/Button';
import { Background, Main, Shade } from 'styles/themeColors';
import { CircularProgress } from '@mui/material';
import { Color, FontWeight } from 'lib/styles';
import { VariantSKUForm } from 'data/Service';
import ImportModal from 'components/UI/ImportModal';
import { handleProductAddonsExport, verifyServiceProductsFileTemplate } from 'utils/addOn';
import { useAPI } from 'lib/API';
import { getExportProductAddons, importServiceProducts } from 'requests/services';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useCountries } from 'hooks/useCountries';
import { STEPS } from 'constants/productVariantImport';
import { UploadingStatus } from 'data/Service';
import { filenameWithoutExtension } from 'components/Pricebooks/priceebook.utils';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';

const LabelTextError = styled.span<any>`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  font-size: 12px;
  color: ${Main.error};
`;

interface Props {
    control: Control<IFormData>;
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    variantForm: VariantSKUForm;
    setVariantForm: (variant: VariantSKUForm) => void;
    onSearch: () => void;
    watch: any;
    invalidSkus: string;
    setInvalidSkus: (invalidSkus: string) => void;
    setValue?: UseFormSetValue<IFormData>;
    isLoading?: boolean;
    serviceId: number;
    vendorId: number;
    existingSkus: string;
    setExistingSkus: (existingSkus: string) => void;
    businessUnitId?: number;
}

export const Message = styled.div`
  font-size: 14px;
  margin-bottom: 10px;
`;

const ServiceProducts: React.FC<Props> = ({ control,
    searchTerm,
    setSearchTerm,
    variantForm,
    setVariantForm,
    onSearch,
    watch,
    invalidSkus,
    setInvalidSkus,
    setValue,
    isLoading = false,
    serviceId,
    vendorId,
    existingSkus,
    setExistingSkus,
    businessUnitId,
}: Props) => {
    const [showImportModal, setShowImportModal] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [excelData, setExcelData] = React.useState<any>();
    const [uploadingStatus, setUploadingStatus] = React.useState<UploadingStatus | null>();
    const [step, setStep] = React.useState(STEPS.UPLOADING);
    const variants = watch(SERVICE_FORM_FIELDS.VARIANTS);
    const [exportedData, setExportedData] = React.useState<any>({});
    const [fileName, setFileName] = React.useState<string>();

    const serviceName = watch(SERVICE_FORM_FIELDS.NAME);

    const { countries } = useCountries();

    const toggleImportModal = () => {
        if (vendorId) {
            setShowImportModal(!showImportModal);
        } else {
            renderToast(toast.TYPE.INFO, SERVICE_CONSTANTS.PLEASE_SELECT_VENDOR, {
                autoClose: 5000
            });
        }
    }

    const uploadServiceProductsData = useAPI({
        deferFn: importServiceProducts,
        onResolve: (result: any) => {
            if (result.data?.length > 0) {
                setValue?.(SERVICE_FORM_FIELDS.VARIANTS, result.data);
                if (serviceId) {
                    getProductAddonsExportReq.run(serviceId, vendorId);
                }
            }
        },
        onReject: (err: any) => {
            renderToast(toast.TYPE.ERROR, err.message, {
                autoClose: 5000
            });
        }
    });

    const getProductAddonsExportReq = useAPI({
        deferFn: getExportProductAddons,
        onResolve: (result: any) => {
            setExportedData(result)
        },
        onReject: (err: any) => {
            renderToast(toast.TYPE.ERROR, err.message, {
                autoClose: 5000
            });
        }
    });

    React.useEffect(() => {
        if (serviceId && vendorId) {
            getProductAddonsExportReq.run(serviceId, vendorId);
        }
    }, [serviceId, vendorId])

    const handleUploadExcelData = () => {
        if (vendorId) {
            setIsUploading(true);
            const payload = {
                data: excelData,
                fileName: filenameWithoutExtension(fileName),
                businessUnitId,
            }
            uploadServiceProductsData.run(vendorId, payload)
        }
    }

    const handleExport = async () => {
        if (vendorId) {
            await handleProductAddonsExport(exportedData, serviceName, countries);
        } else {
            renderToast(toast.TYPE.INFO, SERVICE_CONSTANTS.PLEASE_SELECT_VENDOR, {
                autoClose: 5000
            });
        }
    }

    const importExportButtons = (
        <Flexbox flexDirection='row' alignItems='center' gap='16px' justifyContent='flex-end'>
            <Button isGhost color={Background.navy} borderColor={Shade.shade550} onClick={toggleImportModal} permissionCode={PermissionCode.MAP_ADDONS_PRODUCTS}>Import XLSX</Button>
            <Button isGhost color={Background.navy} borderColor={Shade.shade550} onClick={handleExport} permissionCode={PermissionCode.MAP_ADDONS_PRODUCTS}>Export XLSX</Button>
        </Flexbox>
    );

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(e.target.value);
        if (value > SKU_QUANTITY.MAX) {
            value = SKU_QUANTITY.MAX;
        }
        if (value < SKU_QUANTITY.MIN) {
            value = SKU_QUANTITY.MIN;
        }
        setVariantForm({
            ...variantForm,
            quantity: isNaN(value) ? 1 : value
        });
    };

    // For isOptional (boolean type)
    const handleOptionalChange = (checked: boolean) => {
        setVariantForm({
            ...variantForm,
            isOptional: checked
        });
    };

    const { readOnly: readOnlyAddonProducts } = useReadPermission(PermissionCode.MAP_ADDONS_PRODUCTS);

    return (
        <>
            <Heading>{GENERAL_CONSTANTS.PRODUCTS}</Heading>

            <Flexbox flexDirection='column' gap='8px' margin='12px 0px' width='100%' padding='16px 20px' borderRadius='6px' border={`1px solid ${Color.lighterGray}`}>
                <Flexbox flexDirection='row' gap='8px' width='100%' alignItems='flex-start'>
                    <Flexbox flexDirection='column' width='80%' gap='2px' alignItems='flex-start'>
                        <InputComponent
                            id='skus'
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setInvalidSkus(''); setExistingSkus(''); }}
                            labelName={SERVICE_CONSTANTS.VARIANT_SKU_NUMBER}
                            labelPosition='top'
                            displayAsterisk={false}
                            isInvalid={!!invalidSkus}
                            errorMessage={`${SERVICE_CONSTANTS.SKUS_NOT_FOUND}: ${invalidSkus}`}
                            placeholder={SERVICE_CONSTANTS.ADD_VARIANT_SKU_NUMBER}
                            width='100%'
                            showOptional={false}
                            isOptional
                            readOnly={readOnlyAddonProducts}
                        />
                        {existingSkus && <LabelTextError>{`${SERVICE_CONSTANTS.SKUS_ALREADY_FOUND}: ${existingSkus}`}</LabelTextError>}
                    </Flexbox>
                    <InputComponent
                        id='quantity'
                        value={variantForm.quantity}
                        onChange={handleQuantityChange}
                        labelName={SERVICE_CONSTANTS.VARIANT_QTY}
                        labelPosition='top'
                        displayAsterisk={false}
                        width="8%"
                        type="number"
                        defaultValue={SKU_QUANTITY.MIN.toString()}
                        minValue={SKU_QUANTITY.MIN.toString()}
                        showOptional={false}
                        isOptional
                        readOnly={readOnlyAddonProducts}
                    />
                    <Button
                        margin='28px 0px 0px 0px'
                        onClick={onSearch}
                        background={Background.navy}
                        borderColor={Background.navy}
                        disabled={isLoading}
                        permissionCode={PermissionCode.MAP_ADDONS_PRODUCTS}
                    >
                        {isLoading ? <CircularProgress size="16px" style={{ color: Background.white }} /> : <>{SERVICE_CONSTANTS.ADD_TO_SKU}</>}
                    </Button>
                </Flexbox>
                <Flexbox flexDirection='row' gap='8px' width='100%'>
                    <div>
                        <Controller
                            name={SERVICE_FORM_FIELDS.OPTIONAL_SKU}
                            control={control}
                            render={({ field }) => (
                                renderCheckbox(
                                    {
                                        ...field,
                                        onChange: (checked: boolean) => {
                                            field.onChange(checked); // Update react-hook-form
                                            handleOptionalChange(checked); // Update local state
                                        }
                                    },
                                    SERVICE_FORM_FIELDS.OPTIONAL_SKU,
                                    SERVICE_CONSTANTS.OPTIONAL_FOR_SELECTED_SKU,
                                    '',
                                    PermissionCode.MAP_ADDONS_PRODUCTS,
                                )
                            )}
                        />
                    </div>
                </Flexbox>
            </Flexbox >
            <Flexbox flexDirection='row' justifyContent='space-between' alignItems='center'>
                <Message>{variants.length === 0 ? SERVICE_CONSTANTS.NO_PRODUCTS_ADDED : SERVICE_CONSTANTS.SERVICE_PRODUCTS_LISTING}</Message>
                {variants.length === 0 && importExportButtons}
            </Flexbox>
            {
                variants.length > 0 &&
                <ServiceProductsTable variants={variants} setValue={setValue} importExportButtons={importExportButtons} readOnly={readOnlyAddonProducts} />
            }

            <ImportModal
                show={showImportModal}
                onClose={toggleImportModal}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                uploadData={handleUploadExcelData}
                heading={SERVICE_CONSTANTS.IMPORT_PRODUCT_ADDONS_HEADING}
                setExcelData={setExcelData}
                downloadTemplateFileName={PRODUCT_ADDONS_DOWNLOAD_TEMPLATE}
                fileHeaderValidator={verifyServiceProductsFileTemplate}
                step={step}
                setStep={setStep}
                progressSocket={PRODUCT_ADDONS_IMPORT_PROGRESS_SOCKET}
                importResultSocket={PRODUCT_ADDONS_IMPORT_COMPLETION_SOCKET}
                uploadingStatus={uploadingStatus!}
                setUploadingStatus={setUploadingStatus!}
                label={SERVICE_CONSTANTS.SERVICES}
                container={PRODUCT_ADDONS_IMPORT_CONTAINER}
                loadData={() => setIsUploading(false)}
                setFileName={setFileName}
            />
        </>
    );
}

export default (ServiceProducts);